<template>
  <div class="fa-root">

    <!--  Page header  -->
    <div class="fa-header">
      <div class="d-flex align-center" style="gap:10px">
        <div class="fa-header-icon">
          <v-icon small color="#185FA5">mdi-flag-outline</v-icon>
        </div>
        <div>
          <div class="fa-overline">AML Pro</div>
          <div class="fa-title">Flagged Attempts</div>
        </div>
      </div>
      <div class="d-flex align-center" style="gap:8px">
        <v-chip v-if="hasActiveFilters" x-small close outlined color="primary" @click:close="clearFilters">
          <v-icon x-small left>mdi-filter</v-icon>Filters active
        </v-chip>
        <button class="fa-btn-ghost" @click="filterDialog = true">
          <v-icon x-small left>mdi-filter-outline</v-icon>Filters
        </button>
        <button class="fa-btn-primary" @click="openCreate">
          <v-icon x-small left>mdi-plus</v-icon>Log Attempt
        </button>
      </div>
    </div>

    <!--  Tab bar  -->
    <div class="fa-tabs-bar">
      <button class="fa-tab" :class="{ 'fa-tab--active fa-tab--blue': viewTab === 0 }" @click="viewTab = 0">
        <v-icon x-small left>mdi-link-off</v-icon>
        Unlinked
        <span class="fa-tab-count" :class="viewTab === 0 ? 'fa-count-blue' : ''">{{ unlinkedCount }}</span>
      </button>
      <button class="fa-tab" :class="{ 'fa-tab--active fa-tab--green': viewTab === 1 }" @click="viewTab = 1">
        <v-icon x-small left>mdi-link</v-icon>
        Linked — open cases
        <span class="fa-tab-count" :class="viewTab === 1 ? 'fa-count-green' : ''">{{ linkedOpenCount }}</span>
      </button>
      <button class="fa-tab" :class="{ 'fa-tab--active fa-tab--grey': viewTab === 2 }" @click="viewTab = 2">
        <v-icon x-small left>mdi-lock-outline</v-icon>
        Linked — closed cases
        <span class="fa-tab-count" :class="viewTab === 2 ? 'fa-count-grey' : ''">{{ linkedClosedCount }}</span>
      </button>
    </div>

    <!-- Tab context banner -->
    <div class="fa-banner" :class="viewTab === 0 ? 'fa-banner-blue' : viewTab === 1 ? 'fa-banner-green' : 'fa-banner-grey'">
      <template v-if="viewTab === 0">
        These attempts have <strong>not yet been linked</strong> to any AML case. Review and open a case if warranted.
      </template>
      <template v-else-if="viewTab === 1">
        These attempts are linked to <strong>currently open</strong> AML cases under active investigation.
      </template>
      <template v-else>
        These attempts are linked to <strong>closed</strong> cases. They are read-only and cannot be linked to new cases.
      </template>
    </div>

    <!--  Main table card  -->
    <div class="fa-card">

      <!--  Skeleton loader  -->
      <template v-if="loading">
        <div class="fa-skeleton-wrap">
          <v-skeleton-loader type="table-thead" />
          <v-skeleton-loader type="table-row-divider@6" />
        </div>
      </template>

      <div v-else-if="!currentAttempts.length" class="fa-empty">
        <v-icon size="40" color="grey lighten-2">mdi-flag-off-outline</v-icon>
        <div class="mt-2" style="font-size:13px;color:#aaa">No flagged attempts in this category</div>
      </div>

      <div v-else class="fa-table-wrap">
        <table class="fa-table">
          <!-- Unlinked columns -->
          <template v-if="viewTab === 0">
            <colgroup>
              <col style="width:30%">
              <col style="width:12%">
              <col style="width:12%">
              <col style="width:12%">
              <col style="width:17%">
              <col style="width:13%">
              <col style="width:4%">
            </colgroup>
            <thead>
              <tr>
                <th>Suspicion reason</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Txn date</th>
                <th>Logged by</th>
                <th>Logged</th>
                <th></th>
              </tr>
            </thead>
          </template>
          <!-- Linked columns -->
          <template v-else>
            <colgroup>
              <col style="width:25%">
              <col style="width:11%">
              <col style="width:11%">
              <col style="width:11%">
              <col style="width:14%">
              <col style="width:14%">
              <col style="width:10%">
              <col style="width:4%">
            </colgroup>
            <thead>
              <tr>
                <th>Suspicion reason</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Txn date</th>
                <th>Linked case</th>
                <th>Logged by</th>
                <th>Logged</th>
                <th></th>
              </tr>
            </thead>
          </template>

          <tbody>
            <tr
              v-for="item in currentAttempts"
              :key="item.id"
              class="fa-row"
              @click="goToDetail(item.id)"
            >
              <!-- Suspicion -->
              <td>
                <div class="fa-suspicion-reason">{{ item.suspicionReason }}</div>
                <div v-if="item.suspicionCategory" class="fa-label mt-1">{{ item.suspicionCategory }}</div>
              </td>

              <!-- Type -->
              <td>
                <span class="fa-pill" :style="typePillStyle(item.passThroughType)">{{ formatType(item.passThroughType) }}</span>
              </td>

              <!-- Amount -->
              <td>
                <span v-if="item.amount" class="fa-value">{{ item.currency }} ${{ Number(item.amount).toLocaleString() }}</span>
                <span v-else class="fa-label">—</span>
              </td>

              <!-- Transaction date -->
              <td class="fa-label">{{ item.transactionDate || '—' }}</td>

              <!-- Linked case -->
              <td v-if="viewTab > 0" @click.stop>
                <div v-if="item.caseLinks && item.caseLinks.length">
                  <div v-for="link in item.caseLinks" :key="link.id" class="mb-1">
                    <span
                      class="fa-case-chip"
                      :style="link.case && isClosedCaseStatus(link.case.status) ? 'background:#F1EFE8;color:#5F5E5A;cursor:pointer' : 'background:#EAF3DE;color:#3B6D11;cursor:pointer'"
                      @click="link.case && $router.push({ name: 'aml-case-detail', params: { id: link.caseId } })"
                    >
                      <v-icon x-small style="margin-right:3px">{{ link.case && isClosedCaseStatus(link.case.status) ? 'mdi-lock-outline' : 'mdi-folder-open-outline' }}</v-icon>
                      {{ link.case ? link.case.caseReference : link.caseId.slice(0,8) + '…' }}
                    </span>
                  </div>
                </div>
                <span v-else class="fa-label">—</span>
              </td>

              <!-- Logged by -->
              <td>
                <div class="d-flex align-center" style="gap:6px">
                  <div class="fa-avatar" :style="avatarStyle(assigneeName(item.flaggedBy))">{{ avatarInitials(assigneeName(item.flaggedBy)) }}</div>
                  <span class="fa-label fa-truncate">{{ assigneeName(item.flaggedBy) }}</span>
                </div>
              </td>

              <!-- Date logged -->
              <td>
                <div class="fa-value" style="font-size:11px">{{ timeAgo(item.dateCreated) }}</div>
                <div class="fa-label">{{ fmtDate(item.dateCreated) }}</div>
              </td>

              <!-- Actions -->
              <td @click.stop>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <button class="fa-icon-btn" v-on="on" @click="goToDetail(item.id)">
                      <v-icon x-small color="#185FA5">mdi-eye</v-icon>
                    </button>
                  </template>
                  <span>View detail</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div v-if="!loading" class="fa-table-footer">
        <span>{{ currentAttempts.length }} attempt{{ currentAttempts.length !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- Filter Dialog -->
    <v-dialog v-model="filterDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-filter-outline</v-icon>
            <span class="subtitle-1">Filter flagged attempts</span>
          </div>
          <v-btn icon @click="filterDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select v-model="filters.passThroughType" :items="passThroughTypeOptions" item-text="text" item-value="value" label="Pass-through type" clearable dense outlined prepend-inner-icon="mdi-account-convert-outline" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="filters.reportType" :items="reportTypeOptions" label="Report type" clearable dense outlined prepend-inner-icon="mdi-file-document-outline" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="filters.suspicionCategory" :items="suspicionCategories" label="Suspicion category" clearable dense outlined prepend-inner-icon="mdi-alert-circle-outline" />
            </v-col>
            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-switch v-model="filters.unlinked" label="Unlinked to case only" dense hide-details color="primary" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="filters.dateFrom" label="Date from" type="date" dense outlined prepend-inner-icon="mdi-calendar-start" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="filters.dateTo" label="Date to" type="date" dense outlined prepend-inner-icon="mdi-calendar-end" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn text color="grey darken-1" :disabled="!hasActiveFilters" @click="clearFilters">
            <v-icon left small>mdi-close</v-icon>Clear all
          </v-btn>
          <v-spacer />
          <v-btn text @click="filterDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="loading" @click="applyFiltersAndClose">
            <v-icon left small>mdi-magnify</v-icon>Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Log Attempt Dialog -->
    <v-dialog v-model="createDialog" max-width="720" persistent scrollable>
      <v-card rounded="lg">
        <v-card-title class="justify-space-between align-center">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-flag-plus-outline</v-icon>
            <span class="text-h6 font-weight-medium">Log Flagged Attempt</span>
          </div>
          <v-btn icon @click="closeCreate"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="min-height:500px">
          <v-form ref="createForm">

            <div class="caption grey--text text-uppercase font-weight-medium mt-3 mb-2" style="letter-spacing:.5px">Transaction details</div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select v-model="form.passThroughType" :items="passThroughTypes" label="Pass-through type *" dense outlined :rules="[v => !!v || 'Required']" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.transactionDate" label="Transaction date *" type="date" dense outlined :rules="[v => !!v || 'Required']" />
              </v-col>
              <v-col cols="12" sm="4"><v-select v-model="form.transactionType" :items="transactionTypes" label="Transaction type" dense outlined clearable /></v-col>
              <v-col cols="12" sm="4"><v-select v-model="form.transactionMethod" :items="transactionMethods" label="Transaction method" dense outlined clearable /></v-col>
              <v-col cols="12" sm="4"><v-select v-model="form.currency" :items="currencies" label="Currency *" dense outlined :rules="[v => !!v || 'Required']" /></v-col>

              <v-col cols="12" sm="4">
                <v-text-field
                  :value="amountDisplay"
                  label="Amount"
                  type="text"
                  inputmode="numeric"
                  prefix="$"
                  dense
                  outlined
                  @input="onAmountInput"
                  @change="onAmountChange"
                />
              </v-col>

              <v-col cols="12" sm="4" v-if="isChequeTransaction"><v-text-field v-model="form.chequeNumber" label="Cheque number" dense outlined /></v-col>
              <v-col cols="12" sm="4" v-if="isChequeTransaction || isWireTransaction"><v-text-field v-model="form.bank" label="Bank" dense outlined /></v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.transactionDescription"
                  label="Description"
                  dense
                  outlined
                  rows="2"
                  auto-grow
                  no-resize
                  counter="250"
                  maxlength="250"
                  hint="Max 250 characters"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="form.sourceOfFunds"
                  :items="sourceOfIncomeTypes"
                  item-text="type"
                  item-value="type"
                  label="Source of funds"
                  dense
                  outlined
                  clearable
                  :loading="loadingIncomeTypes"
                  no-data-text="No income types found"
                />
              </v-col>
            </v-row>

            <v-divider class="my-3" />

            <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Suspicion</div>
            <v-row dense>
              <v-col cols="12">
                <v-autocomplete v-model="form.suspicionReason" :items="suspicionBehaviours" item-text="suspiciousBehaviour" item-value="suspiciousBehaviour" label="Suspicion reason * (from suspicious behaviours)" dense outlined :rules="[v => !!v || 'Required']" :loading="loadingBehaviours" no-data-text="No behaviours loaded">
                  <template #item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.suspiciousBehaviour }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.code" class="caption">{{ item.code }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="form.suspicionCategory" :items="suspicionCategories" label="Suspicion category" dense outlined clearable />
              </v-col>
            </v-row>

            <v-divider class="my-3" />

            <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Account</div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-card v-if="form.selectedAccount" outlined class="pa-2 mb-2" color="blue lighten-5">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="caption font-weight-medium">{{ form.selectedAccount.personName }}</div>
                      <div class="caption primary--text">{{ form.selectedAccount.mainAccountNumber }}</div>
                    </div>
                    <v-btn icon x-small @click="clearAccount"><v-icon small>mdi-close-circle</v-icon></v-btn>
                  </div>
                </v-card>
                <v-btn v-else outlined color="primary" x-small @click="accountSearchDialog = true">
                  <v-icon left x-small>mdi-account-search</v-icon>Link account (optional)
                </v-btn>
              </v-col>
            </v-row>

            <!-- ACCOUNT_HOLDER -->
            <template v-if="form.passThroughType === 'ACCOUNT_HOLDER'">
              <v-divider class="my-3" />
              <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Account holder details</div>
              <v-card outlined class="pa-3 mb-2" v-if="form.selectedAccount && form.selectedAccount.person" color="grey lighten-5">
                <v-row dense>
                  <v-col cols="6"><div class="caption grey--text">Name</div><div>{{ form.selectedAccount.person.firstName }} {{ form.selectedAccount.person.lastName }}</div></v-col>
                  <v-col cols="6"><div class="caption grey--text">TRN</div><div>{{ form.selectedAccount.person.trn || '—' }}</div></v-col>
                  <v-col cols="6" v-if="form.selectedAccount.person.dateOfBirth">
                    <div class="caption grey--text">Date of birth</div>
                    <div>{{ fmtDateOnly(form.selectedAccount.person.dateOfBirth) }}</div>
                  </v-col>
                </v-row>
                <div class="caption grey--text mt-1">Details pulled from account record</div>
              </v-card>
            </template>

            <!-- NON_ACCOUNT_HOLDER -->
            <template v-if="form.passThroughType === 'NON_ACCOUNT_HOLDER'">
              <v-divider class="my-3" />
              <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Customer details</div>
              <v-row dense>
                <v-col cols="12" sm="6"><v-text-field v-model="form.firstName"  label="First name"      dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.lastName"   label="Last name"       dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.idNumber"   label="ID number"       dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-select v-model="form.idType" :items="idTypes" label="ID type" dense outlined clearable /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.trn"        label="TRN"             dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.cellNumber" label="Cell number"     dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.country"    label="Country"         dense outlined /></v-col>
                <v-col cols="12">      <v-text-field v-model="form.street"      label="Street"          dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.city"       label="City"            dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.province"   label="Parish/Province" dense outlined /></v-col>
              </v-row>
            </template>

            <!-- SENDER -->
            <template v-if="form.passThroughType === 'SENDER'">
              <v-divider class="my-3" />
              <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Sender details</div>
              <v-row dense>
                <v-col cols="12" sm="6"><v-text-field v-model="form.senderFirstName"     label="First name"      dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.senderLastName"      label="Last name"       dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.senderIdNumber"      label="ID number"       dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-select v-model="form.senderIdType" :items="idTypes" label="ID type" dense outlined clearable /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.senderTRN"           label="TRN"             dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.senderAccountNumber" label="Account number"  dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.senderCellNumber"    label="Cell number"     dense outlined /></v-col>
                <v-col cols="12">      <v-text-field v-model="form.senderStreet"         label="Street"          dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.senderCity"          label="City"            dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.senderProvince"      label="Parish/Province" dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.senderCountry"       label="Country"         dense outlined /></v-col>
              </v-row>
            </template>

            <!-- RECEIVER -->
            <template v-if="form.passThroughType === 'RECEIVER'">
              <v-divider class="my-3" />
              <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Receiver details</div>
              <v-row dense>
                <v-col cols="12" sm="6"><v-text-field v-model="form.receiverFirstName"     label="First name"      dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.receiverLastName"      label="Last name"       dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.receiverIdNumber"      label="ID number"       dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-select v-model="form.receiverIdType" :items="idTypes" label="ID type" dense outlined clearable /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.receiverTRN"           label="TRN"             dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.receiverAccountNumber" label="Account number"  dense outlined /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="form.receiverCellNumber"    label="Cell number"     dense outlined /></v-col>
                <v-col cols="12">      <v-text-field v-model="form.receiverStreet"         label="Street"          dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.receiverCity"          label="City"            dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.receiverProvince"      label="Parish/Province" dense outlined /></v-col>
                <v-col cols="12" sm="4"><v-text-field v-model="form.receiverCountry"       label="Country"         dense outlined /></v-col>
              </v-row>
            </template>

          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCreate">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="submitCreate">Log Attempt</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account search sub-dialog -->
    <v-dialog v-model="accountSearchDialog" max-width="600" scrollable>
      <v-card rounded="lg">
        <v-card-title class="justify-space-between">
          <span class="text-h6 font-weight-medium">Find account holder</span>
          <v-btn icon @click="accountSearchDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-row dense class="mt-2">
            <v-col cols="9">
              <v-text-field v-model="accountSearch.query" label="Search by name or TRN" dense outlined hide-details @keyup.enter="searchAccounts" />
            </v-col>
            <v-col cols="3">
              <v-btn depressed color="primary" small :loading="searchingAccounts" @click="searchAccounts" style="height:40px;width:100%">
                <v-icon small>mdi-magnify</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <div class="mt-3">
            <template v-if="searchingAccounts">
              <v-skeleton-loader type="list-item-two-line@4" />
            </template>
            <template v-else>
              <div v-if="accountSearch.searched && !accountSearch.results.length" class="caption grey--text text-center py-3">No results found.</div>
              <v-list dense v-if="accountSearch.results.length" outlined style="border-radius:8px;overflow:hidden">
                <v-list-item v-for="r in accountSearch.results" :key="r.accountId" dense style="cursor:pointer" @click="selectAccount(r)">
                  <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-account-circle-outline</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="caption font-weight-medium">{{ r.personName }}</v-list-item-title>
                    <v-list-item-subtitle class="caption">{{ r.mainAccountNumber }}<span v-if="r.trn"> · TRN: {{ r.trn }}</span></v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action><v-btn x-small depressed color="primary">Select</v-btn></v-list-item-action>
                </v-list-item>
              </v-list>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import PageMixin    from '@/mixins/page.mixin';
import FormatMixin  from '@/mixins/format.mixin';
import AmlMixin     from '@/mixins/aml.mixin';
import { FlaggedAttemptService }      from '@/modules/amlpro/services/flagged-attempt.service';
import { MemberProfileService }       from '@/modules/amlpro/services/member-profile-service';
import { SuspiciousBehaviourService } from '@/modules/global-settings/aml/services/suspicious-behaviour.service';

const svc          = new FlaggedAttemptService();
const memberSvc    = new MemberProfileService();
const behaviourSvc = new SuspiciousBehaviourService();

const BLANK_FORM = () => ({
  passThroughType: '', transactionDate: '', transactionType: '',
  transactionMethod: '', transactionDescription: '', currency: 'JMD',
  amount: '', chequeNumber: '', bank: '', sourceOfFunds: '',
  suspicionReason: '', suspicionCategory: '', selectedAccount: null,
  accountId: '', mainAccountNumber: '',
  firstName: '', lastName: '', idNumber: '', idType: '', idExpirationDate: '',
  trn: '', street: '', city: '', province: '', country: '', cellNumber: '',
  otherContactNumber: '', accountNumber: '', customerDescription: '',
  senderFirstName: '', senderLastName: '', senderIdNumber: '', senderIdType: '',
  senderIdExpiryDate: '', senderTRN: '', senderStreet: '', senderCity: '',
  senderProvince: '', senderCountry: '', senderCellNumber: '',
  senderAccountNumber: '', senderOtherContactNumber: '',
  receiverFirstName: '', receiverLastName: '', receiverIdNumber: '', receiverIdType: '',
  receiverIdExpiryDate: '', receiverTRN: '', receiverStreet: '', receiverCity: '',
  receiverProvince: '', receiverCountry: '', receiverCellNumber: '',
  receiverAccountNumber: '', receiverOtherContactNumber: '',
});

export default {
  name: 'FlaggedAttempts',
  mixins: [PageMixin, FormatMixin, AmlMixin],

  data() {
    return {
      loading: false, saving: false, loadingBehaviours: false,
      loadingIncomeTypes: false, searchingAccounts: false,
      attempts: [], total: 0,
      tableOptions: { page: 1, itemsPerPage: 20 },
      filters: { passThroughType: null, reportType: null, suspicionCategory: null, dateFrom: '', dateTo: '', unlinked: true },
      filterDialog: false,
      viewTab: 0,
      createDialog: false,
      accountSearchDialog: false,
      form: BLANK_FORM(),
      amountDisplay: '',
      suspicionBehaviours: [],
      sourceOfIncomeTypes: [],
      accountSearch: { query: '', results: [], searched: false },

      passThroughTypes: ['ACCOUNT_HOLDER','NON_ACCOUNT_HOLDER','SENDER','RECEIVER'],
      passThroughTypeOptions: [
        { text: 'Account holder',     value: 'ACCOUNT_HOLDER'     },
        { text: 'Non-account holder', value: 'NON_ACCOUNT_HOLDER' },
        { text: 'Sender',             value: 'SENDER'             },
        { text: 'Receiver',           value: 'RECEIVER'           },
      ],
      reportTypeOptions:   ['SAR','CTR','STR','OTHER'],
      suspicionCategories: ['CASH','WIRE','STRUCTURING','IDENTITY','BEHAVIOUR','OTHER'],
      transactionTypes:    ['DEPOSIT','WITHDRAWAL','TRANSFER','EXCHANGE','PAYMENT','OTHER'],
      transactionMethods:  ['CASH','CHEQUE','WIRE_TRANSFER','ACH','CARD','OTHER'],
      currencies:          ['JMD','USD','GBP','EUR','CAD','TTD','BBD'],
      idTypes:             ['NATIONAL_ID','PASSPORT','DRIVERS_LICENCE','TRN_CARD','VOTER_ID','OTHER'],
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),
    isChequeTransaction() { return this.form.transactionMethod === 'CHEQUE'; },
    isWireTransaction()   { return this.form.transactionMethod === 'WIRE_TRANSFER'; },

    unlinkedAttempts()    { return this.attempts.filter(a => !a.caseLinks || !a.caseLinks.length); },
    linkedOpenAttempts()  { return this.attempts.filter(a => a.caseLinks && a.caseLinks.some(l => l.case && !this.isClosedCaseStatus(l.case.status))); },
    linkedClosedAttempts(){ return this.attempts.filter(a => a.caseLinks && a.caseLinks.length && a.caseLinks.every(l => !l.case || this.isClosedCaseStatus(l.case.status))); },

    unlinkedCount()       { return this.unlinkedAttempts.length; },
    linkedOpenCount()     { return this.linkedOpenAttempts.length; },
    linkedClosedCount()   { return this.linkedClosedAttempts.length; },

    currentAttempts() {
      if (this.viewTab === 1) return this.linkedOpenAttempts;
      if (this.viewTab === 2) return this.linkedClosedAttempts;
      return this.unlinkedAttempts;
    },

    hasActiveFilters() {
      return !!(this.filters.passThroughType || this.filters.reportType || this.filters.suspicionCategory || this.filters.unlinked || this.filters.dateFrom || this.filters.dateTo);
    },
  },

  mounted() {
    this.loadAttempts();
    this.loadBehaviours();
    this.loadIncomeTypes();
  },

  methods: {
    ...mapActions('sourceOfIncomeTypesModule', ['fetchPaginatedSourceOfIncomeTypes']),

    typePillStyle(t) {
      const m = {
        ACCOUNT_HOLDER:     'background:#E6F1FB;color:#185FA5',
        NON_ACCOUNT_HOLDER: 'background:#E1F5EE;color:#0F6E56',
        SENDER:             'background:#FAEEDA;color:#854F0B',
        RECEIVER:           'background:#EEEDFE;color:#534AB7',
      };
      return m[t] || 'background:#F1EFE8;color:#5F5E5A';
    },
    avatarInitials(name) {
      if (!name || name === '—') return '?';
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    },
    avatarStyle(name) {
      const p = [
        'background:#B5D4F4;color:#185FA5',
        'background:#9FE1CB;color:#0F6E56',
        'background:#CECBF6;color:#3C3489',
        'background:#F5C4B3;color:#993C1D',
        'background:#FAC775;color:#854F0B',
      ];
      return p[(name || '').charCodeAt(0) % p.length] || p[0];
    },

    async loadAttempts() {
      this.loading = true;
      try {
        const params = { page: this.tableOptions.page, limit: this.tableOptions.itemsPerPage, ...this.activeFilters() };
        delete params.unlinked;
        const res     = await svc.fetchAll(params);
        const data    = res.data ?? res;
        this.attempts = data.data ?? data;
        this.total    = data.total ?? 0;
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load flagged attempts' });
      } finally {
        this.loading = false;
      }
    },

    activeFilters() {
      const f = {};
      if (this.filters.passThroughType)   f.passThroughType   = this.filters.passThroughType;
      if (this.filters.reportType)        f.reportType        = this.filters.reportType;
      if (this.filters.suspicionCategory) f.suspicionCategory = this.filters.suspicionCategory;
      if (this.filters.dateFrom)          f.dateFrom          = this.filters.dateFrom;
      if (this.filters.dateTo)            f.dateTo            = this.filters.dateTo;
      if (this.filters.unlinked)          f.unlinked          = true;
      return f;
    },

    onTableUpdate(opts) { this.tableOptions = opts; this.loadAttempts(); },

    async loadBehaviours() {
      this.loadingBehaviours = true;
      try {
        const res = await behaviourSvc.fetchAll();
        this.suspicionBehaviours = (res.data ?? res).filter(b => b.isActive);
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load suspicious behaviours' });
      } finally {
        this.loadingBehaviours = false;
      }
    },

    async loadIncomeTypes() {
      this.loadingIncomeTypes = true;
      try {
        const response = await this.fetchPaginatedSourceOfIncomeTypes({ page: 1, limit: 200 });
        this.sourceOfIncomeTypes = (response.data ?? []).filter(t => t.status);
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load income types' });
      } finally {
        this.loadingIncomeTypes = false;
      }
    },

    openCreate() {
      this.form          = BLANK_FORM();
      this.amountDisplay = '';
      this.accountSearch = { query: '', results: [], searched: false };
      this.createDialog  = true;
    },

    closeCreate() { this.createDialog = false; this.$refs.createForm?.reset(); },

    async submitCreate() {
      if (!this.$refs.createForm.validate()) return;
      this.saving = true;
      try {
        const payload = { ...this.form };
        delete payload.selectedAccount;
        payload.reportType = 'SAR';
        Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === null) delete payload[k]; });
        if (this.form.selectedAccount) {
          payload.accountId         = this.form.selectedAccount.accountId;
          payload.mainAccountNumber = this.form.selectedAccount.mainAccountNumber;
          if (this.form.selectedAccount.person && this.form.passThroughType === 'ACCOUNT_HOLDER') {
            payload.firstName = this.form.selectedAccount.person.firstName;
            payload.lastName  = this.form.selectedAccount.person.lastName;
            payload.trn       = this.form.selectedAccount.person.trn;
          }
        }
        await svc.create(payload);
        this.showSnack({ color: 'success', title: 'Logged', message: 'Flagged attempt recorded' });
        this.closeCreate();
        this.loadAttempts();
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to log attempt';
        this.showSnack({ color: 'error', title: 'Error', message: Array.isArray(msg) ? msg.join(', ') : msg });
      } finally {
        this.saving = false;
      }
    },

    async searchAccounts() {
      if (!this.accountSearch.query.trim()) return;
      this.searchingAccounts = true;
      this.accountSearch.results  = [];
      this.accountSearch.searched = false;
      try {
        const q       = this.accountSearch.query.trim();
        const filters = q.match(/^\d/) ? { trn: q } : { firstName: q };
        const res     = await memberSvc.searchMembers(filters, 0, 20);
        const data    = res.data ?? res;
        const persons = data.data ?? data;
        const results = [];
        for (const p of persons) {
          const accts = p.accounts ?? [];
          if (accts.length) {
            for (const pta of accts) {
              const a = pta.account ?? pta;
              results.push({ accountId: a.id, mainAccountNumber: a.mainAccountNumber, personName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(), trn: p.trn ?? '', person: p });
            }
          } else {
            results.push({ accountId: null, mainAccountNumber: '—', personName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(), trn: p.trn ?? '', person: p });
          }
        }
        this.accountSearch.results  = results;
        this.accountSearch.searched = true;
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Search failed' });
      } finally {
        this.searchingAccounts = false;
      }
    },

    selectAccount(r) {
      if (!r.accountId) { this.showSnack({ color: 'warning', title: 'No account', message: 'This person has no linked account' }); return; }
      this.form.selectedAccount  = r;
      this.accountSearchDialog   = false;
      this.accountSearch.results = [];
    },

    clearAccount() { this.form.selectedAccount = null; this.form.accountId = ''; },

    goToDetail(id) { this.$router.push({ name: 'flagged-attempt-detail', params: { id } }); },

    clearFilters() {
      this.filters = { passThroughType: null, reportType: null, suspicionCategory: null, dateFrom: '', dateTo: '', unlinked: false };
      this.loadAttempts();
    },
    applyFiltersAndClose() { this.filterDialog = false; this.loadAttempts(); },

    isClosedCaseStatus(s) { return this.isClosedStatus(s); },

    onAmountInput(val) {
      this.amountDisplay = this.formatNumberInput(val);
      this.form.amount   = this.parseNumberInput(val);
    },

    onAmountChange(val) {
      const num          = this.parseNumberInput(val);
      this.form.amount   = num;
      this.amountDisplay = this.formatNumberInput(num);
    },

    formatNumberInput(val) {
      if (val === null || val === undefined || val === '') return '';
      const cleaned = typeof val === 'string' ? val.replace(/,/g, '') : val;
      if (cleaned === '' || isNaN(cleaned)) return '';
      const [intPart, decimalPart] = String(cleaned).split('.');
      const formattedInt = Number(intPart).toLocaleString('en-US');
      return decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
    },

    parseNumberInput(val) {
      if (val === null || val === undefined || val === '') return '';
      const cleaned = String(val).replace(/[^0-9.]/g, '');
      if (cleaned === '' || cleaned === '.') return '';
      const num = Number(cleaned);
      return isNaN(num) ? '' : num;
    },

    fmtDateOnly(val) {
      if (!val) return '-';
      return String(val).slice(0, 10);
    },
  },
};
</script>

<style scoped>
/* ── Root ── */
.fa-root { padding: 20px 24px 32px; }

/* ── Header ── */
.fa-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; flex-wrap:wrap; gap:10px; }
.fa-header-icon { width:36px; height:36px; border-radius:10px; background:#E6F1FB; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.fa-overline { font-size:10px; font-weight:600; letter-spacing:1px; text-transform:uppercase; color:#aaa; }
.fa-title { font-size:20px; font-weight:500; letter-spacing:-0.3px; }

/* ── Buttons ── */
.fa-btn-ghost   { font-size:12px; padding:6px 14px; border-radius:8px; border:0.5px solid #ddd; background:transparent; color:inherit; cursor:pointer; display:inline-flex; align-items:center; gap:4px; transition:background 0.12s; font-family:inherit; }
.fa-btn-ghost:hover { background:#f5f5f5; }
.fa-btn-primary { font-size:12px; padding:6px 14px; border-radius:8px; border:none; background:#185FA5; color:#fff; cursor:pointer; display:inline-flex; align-items:center; gap:4px; font-weight:500; font-family:inherit; transition:background 0.12s; }
.fa-btn-primary:hover { background:#0C447C; }

/* ── Tab bar ── */
.fa-tabs-bar { display:flex; border-bottom:0.5px solid #e0e0e0; margin-bottom:0; gap:0; overflow-x:auto; }
.fa-tab { font-size:12px; padding:10px 16px; border:none; background:transparent; color:#888; cursor:pointer; border-bottom:2px solid transparent; transition:all 0.15s; white-space:nowrap; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
.fa-tab:hover { color:#333; }
.fa-tab--active { font-weight:500; }
.fa-tab--blue  { color:#185FA5; border-bottom-color:#185FA5; }
.fa-tab--green { color:#3B6D11; border-bottom-color:#3B6D11; }
.fa-tab--grey  { color:#5F5E5A; border-bottom-color:#5F5E5A; }
.fa-tab-count { font-size:10px; font-weight:700; padding:1px 6px; border-radius:10px; background:#F1EFE8; color:#888; }
.fa-count-blue  { background:#E6F1FB; color:#185FA5; }
.fa-count-green { background:#EAF3DE; color:#3B6D11; }
.fa-count-grey  { background:#F1EFE8; color:#5F5E5A; }

/* ── Banner ── */
.fa-banner { font-size:12px; padding:9px 16px; margin:0; border-bottom:0.5px solid #e0e0e0; }
.fa-banner-blue  { background:#EBF3FC; color:#185FA5; }
.fa-banner-green { background:#EAF3DE; color:#3B6D11; }
.fa-banner-grey  { background:#F1EFE8; color:#5F5E5A; }

/* ── Main card ── */
.fa-card { border:0.5px solid #e0e0e0; border-top:none; border-radius:0 0 12px 12px; background:white; overflow:hidden; }
.fa-empty { padding:40px 20px; display:flex; align-items:center; justify-content:center; flex-direction:column; }

/* ── Skeleton wrapper ── */
.fa-skeleton-wrap { padding: 0; overflow: hidden; }

/* ── Table ── */
.fa-table-wrap { overflow-x:auto; }
.fa-table { width:100%; border-collapse:collapse; font-size:12px; }
.fa-table thead tr { border-bottom:0.5px solid #e0e0e0; }
.fa-table th { padding:9px 14px; text-align:left; font-size:10px; font-weight:600; color:#aaa; letter-spacing:.5px; text-transform:uppercase; }
.fa-row { border-bottom:0.5px solid #f0f0f0; cursor:pointer; transition:background 0.1s; }
.fa-row:last-child { border-bottom:none; }
.fa-row:hover { background:#EBF3FC; }
.fa-row td { padding:11px 14px; vertical-align:middle; }
.fa-table-footer { padding:9px 16px; border-top:0.5px solid #e0e0e0; font-size:11px; color:#aaa; }

/* ── Cell content ── */
.fa-suspicion-reason { font-size:12px; font-weight:500; max-width:240px; white-space:normal; word-break:break-word; line-height:1.4; }
.fa-value   { font-size:12px; color:var(--color-text-primary, #1a1a1a); }
.fa-label   { font-size:11px; color:#aaa; }
.fa-truncate { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:110px; }

.fa-pill { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }

.fa-case-chip { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; transition:opacity 0.1s; }
.fa-case-chip:hover { opacity:0.8; }

.fa-avatar { width:22px; height:22px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; font-size:9px; font-weight:700; flex-shrink:0; font-family:monospace; letter-spacing:.5px; }

.fa-icon-btn { width:26px; height:26px; border-radius:6px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; transition:background 0.1s; }
.fa-icon-btn:hover { background:#f0f0f0; }
</style>